// ═══════════════════════════════════════════════════════════════
// BENEFIT TEMPLATE FORM MODAL
// Path: src/components/shows/BenefitTemplateFormModal.jsx
//
// Add/Edit a benefit template. Used by Benefit Templates tab
// in ShowsView. Catches unique constraint violations and
// displays inline error for duplicate names.
//
// Props:
//   template     — existing template object (null = create mode)
//   categories   — array of existing category strings for autocomplete
//   onSave       — async (templateData) => void
//   onClose      — () => void
//
// Blueprint: CMM_Show_Sponsorship_Blueprint_v1_1.md — Phase 3
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { BookOpen } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY } from "../../constants/appConfig";
import { Modal } from "../ui/Modal";
import { FormInput } from "../ui/FormControls";
import { ActionButton } from "../ui/ActionButton";

export const BenefitTemplateFormModal = ({
  template,
  categories = [],
  onSave,
  onClose,
}) => {
  const { palette } = useApp();
  const isEdit = !!template;

  const [name, setName] = useState(template?.name || "");
  const [description, setDescription] = useState(template?.description || "");
  const [category, setCategory] = useState(template?.category || "");
  const [isActive, setIsActive] = useState(
    template?.is_active !== undefined ? template.is_active : true
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);

  const filteredCategories = categories.filter(
    (cat) => cat && cat.toLowerCase().includes(category.toLowerCase()) && cat !== category
  );

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || null,
        category: category.trim() || null,
        isActive,
      });
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save benefit template.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "Edit Benefit Template" : "Add Benefit Template"}
      icon={BookOpen}
      onClose={onClose}
      width={480}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Name */}
        <FormInput
          label="Benefit Name"
          value={name}
          onChange={(val) => {
            setName(val);
            setError(null);
          }}
          placeholder="e.g. Program credit"
          required
        />

        {/* Description */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 600,
              color: palette.text2,
              fontFamily: FONT_FAMILY,
            }}
          >
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Internal staff reference (not shown to sponsors)"
            rows={3}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: FONT_SIZES.md,
              fontFamily: FONT_FAMILY,
              outline: "none",
              background: palette.inputBg,
              color: palette.inputColor,
              border: `1px solid ${palette.inputBorder}`,
              resize: "vertical",
            }}
          />
        </div>

        {/* Category with autocomplete */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            position: "relative",
          }}
        >
          <label
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 600,
              color: palette.text2,
              fontFamily: FONT_FAMILY,
            }}
          >
            Category
          </label>
          <input
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setShowCategorySuggestions(true);
            }}
            onFocus={() => setShowCategorySuggestions(true)}
            onBlur={() =>
              setTimeout(() => setShowCategorySuggestions(false), 150)
            }
            placeholder="e.g. Recognition, Access, Events"
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: FONT_SIZES.md,
              fontFamily: FONT_FAMILY,
              outline: "none",
              background: palette.inputBg,
              color: palette.inputColor,
              border: `1px solid ${palette.inputBorder}`,
            }}
          />
          {showCategorySuggestions && filteredCategories.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                background: palette.cardBg,
                border: `1px solid ${palette.cardBorder}`,
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                zIndex: 10,
                maxHeight: 150,
                overflowY: "auto",
              }}
            >
              {filteredCategories.map((cat) => (
                <div
                  key={cat}
                  onClick={() => {
                    setCategory(cat);
                    setShowCategorySuggestions(false);
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: FONT_SIZES.sm,
                    fontFamily: FONT_FAMILY,
                    color: palette.text1,
                    borderBottom: `1px solid ${palette.cardBorder}`,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = `${palette.accent1}10`)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  {cat}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active toggle */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 0",
          }}
        >
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            id="template-active"
            style={{ width: 16, height: 16, cursor: "pointer" }}
          />
          <label
            htmlFor="template-active"
            style={{
              fontSize: FONT_SIZES.sm,
              color: palette.text1,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
            }}
          >
            Active (visible in catalog picker)
          </label>
        </div>

        {/* Error message */}
        {error && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#dc2626",
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
            }}
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            paddingTop: 8,
          }}
        >
          <ActionButton
            label="Cancel"
            variant="secondary"
            onClick={onClose}
          />
          <ActionButton
            label={saving ? "Saving..." : isEdit ? "Save Changes" : "Add Benefit"}
            onClick={handleSave}
            disabled={saving || !name.trim()}
          />
        </div>
      </div>
    </Modal>
  );
};
